import { z } from 'zod';
import type {
  EmbedCategory,
  EmbeddedScriptPlacement,
} from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import { getEmbeddedScriptExtensionsDir } from '@wix/cli-astro-definitions';
import { VALID_PATH_REGEX } from '../../../dev-center/components/validations/common.js';
import { createEmbeddedScriptFolderValidation } from '../../../dev-center/components/validations/embedded-script.js';
import type { ExtensionDef } from '../generate-schema/types.js';
import { defineField } from '../generate-schema/definitions.js';
import { generateEmbeddedScript } from './generate.js';

const schema = z.object({
  extensionType: z.literal('EMBEDDED_SCRIPT'),
  name: z.string().describe(defineField({ title: 'Script name' })),
  folder: z
    .string()
    .regex(VALID_PATH_REGEX)
    .describe(
      defineField({
        title: 'Script folder',
        description:
          'Lowercase alphanumeric and hyphens only. Must be unique within src/site/embedded-scripts/.',
      })
    ),
  scriptType: z
    .union([
      z.literal('ESSENTIAL').describe(defineField({ title: 'Essential' })),
      z.literal('FUNCTIONAL').describe(defineField({ title: 'Functional' })),
      z.literal('ANALYTICS').describe(defineField({ title: 'Analytics' })),
      z.literal('ADVERTISING').describe(defineField({ title: 'Advertising' })),
    ])
    .describe(
      defineField({
        title: 'Script type',
        description: 'Determines the consent category of the script.',
      })
    ),
  placement: z
    .union([
      z.literal('HEAD').describe(defineField({ title: 'Head' })),
      z.literal('BODY_START').describe(defineField({ title: 'Body Start' })),
      z.literal('BODY_END').describe(defineField({ title: 'Body End' })),
    ])
    .describe(
      defineField({
        title: 'Placement',
        description: 'Where in the page HTML the script is injected.',
      })
    ),
});

export const embeddedScript: ExtensionDef<typeof schema> = {
  title: 'Embedded Script',
  description: 'A script embedded into site pages',
  group: 'Site',
  documentation:
    'https://dev.wix.com/docs/wix-cli/guides/extensions/site-extensions/embedded-scripts/about-embedded-scripts',
  zodSchema: schema,
  validate: async (projectFolder, data) => {
    const result = createEmbeddedScriptFolderValidation(
      getEmbeddedScriptExtensionsDir(projectFolder)
    )(data.folder);
    return result.valid ? null : result.reason;
  },
  // scriptType and placement are Zod string literals but generateEmbeddedScript expects TypeScript enums
  generate: ({ projectFolder, data }) =>
    generateEmbeddedScript(
      projectFolder,
      data.folder,
      data.name,
      data.scriptType as EmbedCategory,
      data.placement as EmbeddedScriptPlacement
    ),
};
